import type { HpChoiceForm } from "../../store/hpChoiceSlice";

export type NoProductHelpField = "typologie" | "alimentation" | "DHWChoice2";
export type NoProductHelpAction = NoProductHelpField | "cascade";
export type NoProductHelpForm = Pick<HpChoiceForm,
    "typologie" | "alimentation" | "typeECS" | "DHWChoice2" | "nbOccupants">;

const helpOptions = [
    { key: "NO_PRODUCT_HELP_TYPE", field: "typologie" },
    { key: "NO_PRODUCT_HELP_ELEC", field: "alimentation" },
    { key: "NO_PRODUCT_HELP_MI", field: "DHWChoice2" },
] as const;

export function isNoProductHelpEnabled(value: unknown): boolean {
    return value === true || value === "true" || value === 1 || value === "1";
}

export function canSwitchHelpField(value: unknown): boolean {
    return String(value) === "1" || String(value) === "2";
}

/** Boiler details must be completed on PAC Choice before another engine call. */
export function getBoilerReliefForm(form: HpChoiceForm): HpChoiceForm {
    return {
        ...form,
        releveChaudiere: "chaudiere",
        releveChaudiere_i18nCode: "OUI",
    };
}

/** Keep existing inputs wherever compatible; missing hybrid details are completed on PAC Choice. */
export function getHybridForm(form: HpChoiceForm): HpChoiceForm {
    const energyLabels: Record<string, string> = {
        gaz: "SELECT_NRG_GAZ",
        propane: "SELECT_NRG_PROPANE",
        fioul: "SELECT_NRG_FIOUL",
    };
    const energy = form.energie && Object.hasOwn(energyLabels, form.energie) ? form.energie : undefined;
    const incompatibleECS = energy === "fioul" && form.typeECS !== "integree";

    return {
        ...form,
        heatPumpType: "3",
        heatPumpType_i18nCode: "HYBRID",
        utilisation: "2",
        utilisation_i18nCode: "SELECT_UTI_RENO",
        energie: energy,
        energie_i18nCode: energy ? energyLabels[energy] : "",
        releveChaudiere: "sans",
        releveChaudiere_i18nCode: "NON",
        ...(incompatibleECS ? { typeECS: "", typeECS_i18nCode: "" } : {}),
    };
}

/** Engine DHWChoice maps to typeECS; NbPeople maps to nbOccupants. */
export function getIndoorModuleAlternative(form: Pick<NoProductHelpForm, "typeECS" | "DHWChoice2" | "nbOccupants">) {
    const { typeECS, DHWChoice2, nbOccupants } = form;
    const specificModules: Record<string, readonly string[]> = {
        sans: ["classique", "compact"],
        deportee: ["classique", "compact"],
        integree: ["sol", "mural"],
    };
    if (!Object.hasOwn(specificModules, typeECS)) return null;

    const nextType = DHWChoice2 === "ind"
        ? typeECS === "integree" ? "deportee" : "integree"
        : specificModules[typeECS].includes(DHWChoice2) ? typeECS : null;
    if (!nextType) return null;

    const labels: Record<string, string> = {
        sans: "SELECT_ECS_SANS",
        deportee: "SELECT_ECS_DEP",
        integree: "SELECT_ECS_INT",
    };
    return {
        typeECS: nextType,
        typeECS_i18nCode: labels[nextType],
        DHWChoice2: "ind",
        DHWChoice2_i18nCode: "IDU_TYPE_DUOI",
        nbOccupants: typeECS === "sans" && DHWChoice2 === "ind" ? "2" : nbOccupants,
    };
}

/** Use the same form for calculation and persistence to keep navigation consistent. */
export function getNoProductHelpForm(form: HpChoiceForm, field: NoProductHelpAction): HpChoiceForm | null {
    if (field === "cascade") {
        return {
            ...form,
            typologie: "0",
            typologie_i18nCode: "SELECT_TYPO_IND",
            alimentation: "2",
            alimentation_i18nCode: "SELECT_ALIM_TRI",
            ...(form.typeECS === "integree" ? {
                typeECS: "deportee",
                typeECS_i18nCode: "SELECT_ECS_DEP",
            } : {}),
            DHWChoice2: "ind",
            DHWChoice2_i18nCode: "IDU_TYPE_DUOI",
        };
    }
    if (field === "DHWChoice2") {
        const alternative = getIndoorModuleAlternative(form);
        return alternative ? { ...form, ...alternative } : null;
    }
    if (!canSwitchHelpField(form[field])) return null;

    const nextValue = String(form[field]) === "1" ? "2" : "1";
    if (field === "typologie") {
        return {
            ...form,
            typologie: nextValue,
            typologie_i18nCode: nextValue === "1" ? "SELECT_TYPO_SPLIT" : "SELECT_TYPO_MONOBLOC",
            DHWChoice2: "ind",
            DHWChoice2_i18nCode: "IDU_TYPE_DUOI",
        };
    }

    return {
        ...form,
        alimentation: nextValue,
        alimentation_i18nCode: nextValue === "1" ? "SELECT_ALIM_MONO" : "SELECT_ALIM_TRI",
    };
}

export function getNoProductHelpOptions(form: NoProductHelpForm, counts: unknown) {
    return helpOptions.map((option, index) => {
        const value = Number(Array.isArray(counts) ? counts[index] : 0);
        const canShowResults = option.field === "DHWChoice2"
            ? getIndoorModuleAlternative(form) !== null
            : canSwitchHelpField(form[option.field]);
        const count = canShowResults && Number.isSafeInteger(value) && value >= 0 ? value : 0;
        return { ...option, count };
    });
}